/**
 * 채팅 메시지에 링크/첨부된 "마크다운 문서" 감지 + 본문 텍스트 fetch 순수 util.
 * (chat-doc-preview — 2026-06-30, 프론트 단독·additive)
 *
 * 목적:
 *   - 메시지의 첨부(.md) 또는 본문 내 http(s) .md URL 을 문서 descriptor 로 추출한다.
 *   - 우측 미리보기 패널이 이 descriptor 로 원문 텍스트를 가져와 renderMessageMarkdown
 *     (react-markdown + remark-gfm + Mermaid) 로 렌더한다 → md + GitHub mermaid.js 미리보기.
 *
 * 정책:
 *   - http/https 만. GitHub `blob` URL 은 `raw.githubusercontent.com` 으로 변환해 원문 fetch.
 *   - 프론트 단독이라 임의 사이트는 CORS 로 fetch 실패할 수 있다 — 호출부가 graceful 폴백.
 *     (첨부=MinIO presigned GET, GitHub raw 는 ACAO 허용이라 주 케이스는 동작.)
 *   - 기존 렌더 경로(AttachmentList / LinkPreviewCard / renderMessageMarkdown) 무변경.
 */

import { narrowAttachments } from './attachments';
import { requestPresignedDownload } from '../api/files';
import type { ChatMessageEvent } from '../types/chat';
import type { MessageAttachment } from '../types/api';

/** 미리보기 가능한 단일 마크다운 문서 descriptor. */
export interface MarkdownDoc {
  /** 메시지 내 안정 키 (미리보기 store + react key). */
  key: string;
  /** 표시용 제목 (파일명 또는 URL 마지막 세그먼트). */
  title: string;
  source:
    | { kind: 'attachment'; fileId: string }
    | { kind: 'url'; url: string };
}

/** 파일명/경로가 .md / .markdown 으로 끝나는지 (쿼리·해시 허용). */
const MD_EXT_RE = /\.(md|markdown)(?:[?#].*)?$/i;

/** 본문에서 http(s) URL 토큰을 느슨하게 추출 (괄호·꺾쇠·공백 제외). */
const URL_RE = /https?:\/\/[^\s<>()]+/gi;

export function isMarkdownAttachment(a: MessageAttachment): boolean {
  const name = a.name ?? '';
  const mime = a.mimeType ?? '';
  return MD_EXT_RE.test(name) || mime === 'text/markdown' || mime === 'text/x-markdown';
}

/** http/https + (github blob | raw | 경로가 .md) 이면 마크다운 URL 로 간주. */
export function isMarkdownUrl(url: string): boolean {
  try {
    const u = new URL(url);
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return false;
    if (/(^|\.)github\.com$/i.test(u.hostname) && /\/blob\/.+\.(md|markdown)$/i.test(u.pathname)) return true;
    return MD_EXT_RE.test(u.pathname);
  } catch {
    return false;
  }
}

/** GitHub `…/blob/<branch>/<path>` → `raw.githubusercontent.com/…/<branch>/<path>`. 그 외는 원본 유지. */
export function toRawMarkdownUrl(url: string): string {
  try {
    const u = new URL(url);
    if (/(^|\.)github\.com$/i.test(u.hostname)) {
      const m = u.pathname.match(/^\/([^/]+)\/([^/]+)\/blob\/(.+)$/);
      if (m) return `https://raw.githubusercontent.com/${m[1]}/${m[2]}/${m[3]}`;
    }
    return url;
  } catch {
    return url;
  }
}

/** 본문 텍스트에서 마크다운 URL 들을 (중복 제거하여) 추출. */
export function extractMarkdownUrls(content: string | null | undefined): string[] {
  if (!content) return [];
  const out: string[] = [];
  const seen = new Set<string>();
  const matches = content.match(URL_RE) ?? [];
  for (const m of matches) {
    // 문장 끝 구두점이 URL 에 붙어 잡히는 경우 절단.
    const url = m.replace(/[),.;]+$/, '');
    if (isMarkdownUrl(url) && !seen.has(url)) {
      seen.add(url);
      out.push(url);
    }
  }
  return out;
}

function urlTitle(url: string): string {
  try {
    const u = new URL(url);
    const tail = u.pathname.split('/').filter(Boolean).pop();
    return tail ? decodeURIComponent(tail) : u.host;
  } catch {
    return url;
  }
}

/**
 * 메시지에서 미리보기 가능한 마크다운 문서 목록을 추출.
 * 첨부(.md) 우선 + 본문 내 .md URL. 삭제(톰스톤) 메시지는 본문/첨부가 블랭크라 자연히 빈 배열.
 */
export function extractMarkdownDocs(message: ChatMessageEvent): MarkdownDoc[] {
  const docs: MarkdownDoc[] = [];
  for (const a of narrowAttachments(message.attachments)) {
    if (isMarkdownAttachment(a)) {
      docs.push({
        key: `att:${a.fileId}`,
        title: a.name ?? 'document.md',
        source: { kind: 'attachment', fileId: a.fileId },
      });
    }
  }
  for (const url of extractMarkdownUrls(message.content)) {
    docs.push({ key: `url:${url}`, title: urlTitle(url), source: { kind: 'url', url } });
  }
  return docs;
}

/** descriptor → 원문 마크다운 텍스트. 실패 시 throw (호출부가 에러 표시). */
export async function fetchMarkdownText(doc: MarkdownDoc): Promise<string> {
  let target: string;
  if (doc.source.kind === 'attachment') {
    const res = await requestPresignedDownload(doc.source.fileId, { purpose: 'preview' }); // 마크다운 미리보기 = 「열람」(§3.4.1)
    target = res.downloadUrl;
  } else {
    target = toRawMarkdownUrl(doc.source.url);
  }
  const r = await fetch(target, { headers: { Accept: 'text/plain, text/markdown, */*' } });
  if (!r.ok) throw new Error(`HTTP ${r.status}`);
  return await r.text();
}
